/** Shows the editor document's pixels on the export canvas with the selection overlay above, and forwards pointer input in image coordinates. */
import React, {CSSProperties, forwardRef, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import cn from 'classnames';
import {mergeRefs} from 'react-advanced-cropper';
import {isPointWithinBounds, mapPointToImage, PixelBuffer, Point} from './imageEditing';
import {SelectionMask} from './selectionMask';
import {createSelectionOverlay, OVERLAY_MAX_SIDE, SelectionOverlayStyle} from './selectionOverlay';

/** A mouse event translated into native image coordinates. */
export interface ImagePointerEvent {
   point: Point;
   clientX: number;
   clientY: number;
   shiftKey: boolean;
   altKey: boolean;
}

/** Tool callbacks for a primary-button press that starts on the image, the drag that follows and its release. */
export interface ImagePointerHandlers {
   onPointerDown: (event: ImagePointerEvent) => void;
   onPointerMove?: (event: ImagePointerEvent) => void;
   onPointerUp?: (event: ImagePointerEvent) => void;
}

/** Document state the cropper background needs to show and edit the image. */
export interface DocumentViewProps {
   pixels: PixelBuffer | null;
   pixelsVersion: number;
   selection: SelectionMask | null;
   overlayStyle: SelectionOverlayStyle;
   /** Omitted when the active tool does not edit by pointer, which leaves the mouse to the cropper. */
   pointerHandlers?: ImagePointerHandlers;
   onImageLoad: (image: HTMLImageElement) => void;
   onImageError: () => void;
}

interface Props extends DocumentViewProps {
   src?: string;
   className?: string;
   crossOrigin?: 'anonymous' | 'use-credentials' | boolean;
   style?: CSSProperties;
   rotation?: number;
}

/** The cropper's own background-image class, so both canvases are positioned exactly like its default image. */
const BACKGROUND_IMAGE_CLASS = 'advanced-cropper-background-image';

/** Copies a pixel buffer onto a canvas, resizing the canvas to match. */
const drawPixels = (canvas: HTMLCanvasElement, pixels: PixelBuffer): void => {
   if (canvas.width !== pixels.width) {
      canvas.width = pixels.width;
   }
   if (canvas.height !== pixels.height) {
      canvas.height = pixels.height;
   }
   canvas.getContext('2d')?.putImageData(new ImageData(pixels.data, pixels.width, pixels.height), 0, 0);
};

/** Renders the export canvas, the selection overlay and the hidden source image, and routes pointer input to the active tool. */
export const AdjustableImage = forwardRef<HTMLCanvasElement, Props>(
   (
      {
         src,
         className,
         crossOrigin,
         style,
         rotation = 0,
         pixels,
         pixelsVersion,
         selection,
         overlayStyle,
         pointerHandlers,
         onImageLoad,
         onImageError
      }: Props,
      ref
   ) => {
      const canvasRef = useRef<HTMLCanvasElement>(null);
      const overlayRef = useRef<HTMLCanvasElement>(null);
      // Window listeners read the latest handlers and rotation through refs, so they are not
      // re-registered (and do not lose an active drag) on every render.
      const handlersRef = useRef(pointerHandlers);
      const rotationRef = useRef(rotation);
      handlersRef.current = pointerHandlers;
      rotationRef.current = rotation;
      const capturesPointer = pointerHandlers !== undefined;

      const overlay = useMemo(
         () => selection ? createSelectionOverlay(selection, OVERLAY_MAX_SIDE, overlayStyle) : null,
         [selection, overlayStyle]
      );

      useLayoutEffect(() => {
         if (canvasRef.current && pixels) {
            drawPixels(canvasRef.current, pixels);
         }
      }, [pixels, pixelsVersion]);

      useLayoutEffect(() => {
         if (overlayRef.current && overlay) {
            drawPixels(overlayRef.current, overlay);
         }
      }, [overlay]);

      useEffect(() => {
         const canvas = canvasRef.current;
         if (!capturesPointer || !canvas) {
            return;
         }
         let dragging = false;

         /** Translates a mouse event into native image coordinates, honouring the cropper's rotation. */
         const toImageEvent = (event: MouseEvent): ImagePointerEvent => ({
            point: mapPointToImage(
               event.clientX,
               event.clientY,
               canvas.getBoundingClientRect(),
               canvas.width,
               canvas.height,
               rotationRef.current
            ),
            clientX: event.clientX,
            clientY: event.clientY,
            shiftKey: event.shiftKey,
            altKey: event.altKey
         });

         /** Starts a drag only for primary-button presses on the visible image inside this cropper. */
         const handleMouseDown = (event: MouseEvent) => {
            const cropperElement = canvas.closest('.advanced-cropper');
            if (
               event.button !== 0 ||
               !(event.target instanceof Node) ||
               !cropperElement?.contains(event.target) ||
               !isPointWithinBounds(event.clientX, event.clientY, canvas.getBoundingClientRect())
            ) {
               return;
            }
            dragging = true;
            handlersRef.current?.onPointerDown(toImageEvent(event));
         };

         /** Ends the drag and reports the release to the active tool. */
         const endDrag = (event: MouseEvent) => {
            dragging = false;
            handlersRef.current?.onPointerUp?.(toImageEvent(event));
         };

         /** Forwards a drag; a move without the primary button means its release was lost (e.g. alt-tab), so the drag ends. */
         const handleMouseMove = (event: MouseEvent) => {
            if (!dragging) {
               return;
            }
            if ((event.buttons & 1) === 0) {
               endDrag(event);
            } else {
               handlersRef.current?.onPointerMove?.(toImageEvent(event));
            }
         };

         /** Ends an active drag when the mouse button is released anywhere in the window. */
         const handleMouseUp = (event: MouseEvent) => {
            if (dragging) {
               endDrag(event);
            }
         };

         window.addEventListener('mousedown', handleMouseDown);
         window.addEventListener('mousemove', handleMouseMove);
         window.addEventListener('mouseup', handleMouseUp);
         return () => {
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
         };
      }, [capturesPointer, src]);

      return (
         <>
            <canvas
               key={`${src}-canvas`}
               ref={mergeRefs([ref, canvasRef])}
               className={cn(BACKGROUND_IMAGE_CLASS, 'adjustable-image-element', className)}
               style={style}
            />
            {overlay ? (
               <canvas
                  ref={overlayRef}
                  className={BACKGROUND_IMAGE_CLASS}
                  style={{...style, pointerEvents: 'none'}}
               />
            ) : null}
            {src ? (
               <img
                  key={`${src}-img`}
                  style={{display: 'none'}}
                  src={src}
                  crossOrigin={crossOrigin === true ? 'anonymous' : crossOrigin || undefined}
                  onLoad={event => onImageLoad(event.currentTarget)}
                  onError={onImageError}
                  alt=""
               />
            ) : null}
         </>
      );
   }
);

AdjustableImage.displayName = 'AdjustableImage';
